import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthState, AuthUser } from './types';
import {
  bootstrap,
  createWorkspace,
  deletePictureAuthUser,
  loginEmail,
  loginGoogle,
  logout,
  registerEmail,
  updateAuthUser,
  updatePictureAuthUser,
} from './authThunks';

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  bootstrapped: false,
  status: 'idle',
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (b) => {
    b.addCase(bootstrap.pending, (s) => {
      s.loading = true;
      s.error = null;
      s.status = 'loading';
    });
    b.addCase(bootstrap.fulfilled, (s, a: PayloadAction<AuthUser | null>) => {
      s.user = a.payload;
      s.loading = false;
      s.bootstrapped = true;
      s.status = a.payload ? 'authenticated' : 'unauthenticated';
    });
    b.addCase(bootstrap.rejected, (s, a) => {
      s.loading = false;
      s.error = a.error.message ?? 'Bootstrap failed';
      s.bootstrapped = true;
      s.status = 'unauthenticated';
    });
    b.addCase(loginEmail.pending, (s) => {
      s.loading = true;
      s.error = null;
      s.status = 'loading';
    });
    b.addCase(loginEmail.fulfilled, (s, a: PayloadAction<AuthUser>) => {
      s.user = a.payload;
      s.loading = false;
      s.status = 'authenticated';
      s.bootstrapped = true;
    });
    b.addCase(loginEmail.rejected, (s, a) => {
      s.loading = false;
      const payload = a.payload as { message?: string } | undefined;
      s.error = payload?.message ?? a.error.message ?? 'Login failed';
      s.status = 'unauthenticated';
    });
    b.addCase(loginGoogle.pending, (s) => {
      s.loading = true;
      s.error = null;
      s.status = 'loading';
    });
    b.addCase(loginGoogle.fulfilled, (s, a: PayloadAction<AuthUser>) => {
      s.user = a.payload;
      s.loading = false;
      s.status = 'authenticated';
      s.bootstrapped = true;
    });
    b.addCase(loginGoogle.rejected, (s, a) => {
      s.loading = false;
      const payload = a.payload as { message?: string } | undefined;
      s.error = payload?.message ?? a.error.message ?? 'Login failed';
      s.status = 'unauthenticated';
    });
    b.addCase(logout.fulfilled, (s) => {
      s.user = null;
      s.status = 'unauthenticated';
      s.loading = false;
      s.error = null;
      s.bootstrapped = true;
    });
    b.addCase(registerEmail.fulfilled, (state, action) => {
      state.user = action.payload;
      state.status = 'idle';
      state.error = null;
    });
    b.addCase(updateAuthUser.pending, (s) => {
      s.loading = false;
      s.error = null;
    });
    b.addCase(updateAuthUser.fulfilled, (s, a: PayloadAction<AuthUser>) => {
      s.user = a.payload;
      s.loading = false;
      s.error = null;
    });
    b.addCase(updateAuthUser.rejected, (s, a) => {
      s.loading = false;
      const payload = a.payload as { message?: string } | undefined;
      s.error = payload?.message ?? a.error.message ?? 'Update failed';
    });

    b.addCase(updatePictureAuthUser.pending, (s) => {
      s.loading = false;
      s.error = null;
    });
    b.addCase(updatePictureAuthUser.fulfilled, (s, a: PayloadAction<AuthUser>) => {
      s.user = a.payload;
      s.loading = false;
      s.error = null;
    });
    b.addCase(updatePictureAuthUser.rejected, (s, a) => {
      s.loading = false;
      const payload = a.payload as { message?: string } | undefined;
      s.error = payload?.message ?? a.error.message ?? 'Picture update failed';
    });

    b.addCase(deletePictureAuthUser.pending, (s) => {
      s.loading = false;
      s.error = null;
    });
    b.addCase(deletePictureAuthUser.fulfilled, (s, a: PayloadAction<AuthUser>) => {
      s.user = a.payload;
      s.loading = false;
      s.error = null;
    });
    b.addCase(deletePictureAuthUser.rejected, (s, a) => {
      s.loading = false;
      const payload = a.payload as { message?: string } | undefined;
      s.error = payload?.message ?? a.error.message ?? 'Picture delete failed';
    });

    b.addCase(createWorkspace.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(createWorkspace.fulfilled, (s, a: PayloadAction<AuthUser>) => {
      s.user = a.payload;
      s.loading = false;
      s.error = null;
    });
    b.addCase(createWorkspace.rejected, (s, a) => {
      s.loading = false;
      const payload = a.payload as { message?: string } | undefined;
      s.error = payload?.message ?? a.error.message ?? 'Workspace creation failed';
    });
  },
});

export default slice.reducer;
